import React, { useEffect } from "react";
import { Suspense } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import StepNode from "./StepNode";
import { pingTask } from "../../../../util/apiService";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { SharedHistoryContext } from "../../../../common/RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../../../common/RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../../../common/RichTextEditor/LexicalEditor/context/ToolbarContext";
import Editor from "../../../../common/RichTextEditor/Editor/Editor";
import { buildImportMap } from "../../../../util/lexical";
import PlaygroundNodes from "../../../../common/RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import {
  NODE_SELECTION_BLUE,
  getHaloWrapperStyle,
  getHaloCheckboxStyle,
} from "./nodeSelectionCheckboxStyles";

const SectionNode = ({ section, sectionIndex, displaySectionOrdinal, globalStepStart = 0, onNavigate, checkboxState, disabled = false, showCheckbox = true, showOnlySectionCheckboxes = false, checkboxLabelColor = null, allNodesData = [], showSectionCheckbox = true, hideChildCheckboxes = false, forceShowChildHeadings = false, hideSubstepCheckboxes = false, isLock = false, isRevisionLocked = false, hideChildHeadingsForTypes = null, trainedStepNumbers = null, partiallyTrainedStepNumbers = null, allStepsTrained = false, totalQtyToBuild = null, enableCanvasStagePopup = false, documentType = "", documentPartNumber = "", documentRevision = "" }) => {
  const authUser = useSelector((state) => state.auth.user);
  const headingN =
    typeof displaySectionOrdinal === "number" && displaySectionOrdinal >= 1
      ? displaySectionOrdinal
      : sectionIndex + 1;
  const sectionHeading = `Section ${section.is_deleted ? "(Deleted)" : headingN}`;
  
  // Get revisionId and authUser for ping API
  const location = useLocation();
  const params = useParams();
  
  // Extract revisionId from params (could be revisionId or revision_id depending on route)
  const revisionId = params.revisionId || params.revision_id;
  
  // Check if we're on a view-revision route
  const isViewRevisionRoute = location.pathname.includes('/view-revision') || 
                               location.pathname.includes('/view-section') ||
                               location.pathname.includes('/view-step') ||
                               location.pathname.includes('/view-substep') ||
                               location.pathname.includes('/view-childnode');
  
  // Start ping interval if on view-revision route
  useEffect(() => {
    if (!isViewRevisionRoute || !revisionId || !authUser) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed in SectionNode:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [isViewRevisionRoute, revisionId, authUser]);

  // Determine checkbox state for this section (focus id may be tree row or entity ref from audit)
  const focusId = checkboxState?.currentNodeId;
  const sd = section.detail || {};
  const sectionIdsMatch =
    !!focusId &&
    (focusId === section.tree_node_id ||
      focusId === section.section_id_ref ||
      focusId === sd.section_tree_node_id ||
      focusId === sd.procedure_tree_ref);
  const isSectionFocus =
    checkboxState?.currentNodeType === "section" &&
    (sectionIdsMatch || checkboxState?.allChildrenChecked);
  const isSourceSection = checkboxState?.sourceNodeId === section.tree_node_id;
  const isChecked = !!(
    isSectionFocus ||
    checkboxState?.allChildrenChecked ||
    isSourceSection
  );
  const useSelectionHalo =
    isChecked &&
    !isSourceSection &&
    isSectionFocus &&
    checkboxState?.auditPreviewCurrentNode === true;
  // Section checkbox should be enabled even when allChildrenDisabled is true
  // allChildrenDisabled only affects child nodes (steps, substeps), not the section itself
  // So we ignore allChildrenDisabled when determining if section is disabled
  // But disable if revision is locked
  const isDisabled = disabled || (isRevisionLocked && isRevisionLocked.lock && isRevisionLocked.lock.user_id !== authUser?.id);

  const sectionCheckboxOnChange = () => {
    if (isSourceSection) return;
    if (!isDisabled && onNavigate) {
      onNavigate("section", section, {
        sectionIndex,
        sectionHeading,
        sectionId: section.tree_node_id,
      });
    }
  };

  return (
    <div
      key={section.tree_node_id}
      data-export-block="section"
      style={{
        margin: "20px 0",
        clear: "both",
      }}
    >
      <div data-export-block="section-intro">
        <div
          style={{
            clear: "both",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {showCheckbox && showSectionCheckbox && (
            <div style={{ position: "relative", display: "inline-block" }}>
              {useSelectionHalo ? (
                <div style={getHaloWrapperStyle()}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={sectionCheckboxOnChange}
                    style={getHaloCheckboxStyle({
                      disabled: isDisabled,
                      isSource: isSourceSection,
                    })}
                  />
                </div>
              ) : (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={sectionCheckboxOnChange}
                  style={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    accentColor: isSourceSection
                      ? "rgb(68, 68, 68)"
                      : isChecked
                        ? NODE_SELECTION_BLUE
                        : undefined,
                  }}
                />
              )}
              {/* Red cross icon overlay - shown when disabled and hovering */}
              {isDisabled && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    opacity: 0,
                    transition: "opacity 0.2s ease-in-out",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = "0";
                  }}
                  className="checkbox-hover-overlay"
                >
                  <FontAwesomeIcon
                    icon={faTimes}
                    size="sm"
                    color="red"
                    style={{
                      filter: "drop-shadow(0 0 1px rgba(255,255,255,0.8))",
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <h2
            onClick={() => {
              // Prevent action for source section being moved
              if (isSourceSection) {
                return;
              }
              if (!isDisabled && showCheckbox && showSectionCheckbox && onNavigate) {
                onNavigate("section", section, {
                  sectionIndex,
                  sectionHeading,
                  sectionId: section.tree_node_id
                });
              }
            }}
            style={{
              fontSize: "20px",
              margin: 0,
              fontWeight: useSelectionHalo ? 700 : undefined,
              color: useSelectionHalo
                ? "#111"
                : showSectionCheckbox && checkboxLabelColor
                  ? checkboxLabelColor
                  : undefined,
              cursor: isSourceSection ? "not-allowed" : ((!isDisabled && showCheckbox && showSectionCheckbox) ? "pointer" : "default"),
              userSelect: "none"
            }}
          >{sectionHeading}</h2>
        </div>
        <hr />
        <hr />
        <div>{section.detail?.section_name}</div>
        {/* Short Description rendered via Lexical read-only viewer for exact UI parity */}
      {(() => {
        const rawShort = section.detail?.short_description || "";
        // Convert plain text with quotes to HTML format for proper rendering
        const contentHtml =
          typeof rawShort === "string" && rawShort.includes('class="editor-shell"')
            ? rawShort.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1")
            : rawShort && rawShort.trim() !== ""
              ? rawShort.includes('<') && rawShort.includes('>')
                ? rawShort // Already HTML, use as-is
                : `<p>${rawShort.replace(/"/g, '&quot;')}</p>` // Plain text, wrap in p tags
              : rawShort;
        return contentHtml ? (
          <div style={{ marginTop: "5px" }}>
            <div className="rich-text-content-display">
              <Suspense fallback={<div>Loading...</div>}>
                <LexicalComposer
                  initialConfig={{
                    editorState: null,
                    html: { import: buildImportMap() },
                    namespace: "ShortDescriptionViewer",
                    nodes: [...PlaygroundNodes],
                    onError: (error) => {
                      console.error("Error in Short Description viewer:", error);
                    },
                    theme: PlaygroundEditorTheme,
                    readOnly: true,
                    editable: false,
                  }}
                >
                  <SharedHistoryContext>
                    <TableContext>
                      <ToolbarContext>
                        <div className="editor-shell">
                          <Editor
                            handleEditorChange={() => {}}
                            initialContent={contentHtml}
                          />
                        </div>
                      </ToolbarContext>
                    </TableContext>
                  </SharedHistoryContext>
                </LexicalComposer>
              </Suspense>
            </div>
          </div>
        ) : null;
      })()}
      </div>

      {/* Render Steps */}
      {section.steps?.map((step, stepIndex) => {
        const globalStepNumber = globalStepStart + stepIndex + 1;
        return (
          <StepNode
            key={step.tree_node_id}
            step={step}
            stepIndex={stepIndex}
            globalStepNumber={globalStepNumber}
            sectionIndex={sectionIndex}
            sectionHeading={sectionHeading}
            sectionId={section.tree_node_id}
            documentType={documentType}
            documentPartNumber={documentPartNumber}
            documentRevision={documentRevision}
            onNavigate={onNavigate}
            checkboxState={checkboxState}
            disabled={disabled}
            showCheckbox={showCheckbox && !showOnlySectionCheckboxes}
            showOnlySectionCheckboxes={showOnlySectionCheckboxes}
            checkboxLabelColor={checkboxLabelColor}
            allNodesData={allNodesData}
            hideChildCheckboxes={hideChildCheckboxes}
            forceShowChildHeadings={forceShowChildHeadings}
            hideSubstepCheckboxes={hideSubstepCheckboxes}
            isLock={isLock}
            isRevisionLocked={isRevisionLocked}
            hideChildHeadingsForTypes={hideChildHeadingsForTypes}
            trainedStepNumbers={trainedStepNumbers}
            partiallyTrainedStepNumbers={partiallyTrainedStepNumbers}
            allStepsTrained={allStepsTrained}
            totalQtyToBuild={totalQtyToBuild}
            enableCanvasStagePopup={enableCanvasStagePopup}
          />
        );
      })}
    </div>
  );
};

export default SectionNode;

